// "Download as HTML" on the client's report page (report fabf0c2e). The reporter settled what the file is: "The entire
// report must work offline even if the share link is later removed". /dd/view/:token only works live, because its
// attachments come through the share link and pdf.js draws its PDFs in the browser. So this copies the page the client
// is looking at and writes ONE file that needs neither. The client's browser builds it: no server route is involved.
//
// What goes in, and how:
//   · the rendered page (DDPublicPage's root: the title band over .dd-client-report), already one scrolling page;
//   · the files the report SHOWS and no others. FileImageLarge and UploadF mark each where it is drawn (data-dd-box,
//     data-dd-row) and they are read from the public route, which has already left source.* out. A picture goes in as
//     itself, a PDF as a JPEG of EVERY page (no lightbox 20-page cap), anything else as a link that saves the file;
//   · any other picture on the page (the zoning map);
//   · the CSS, with its fonts inside it. A font that cannot be fetched is left out and the page uses the next name in
//     the font stack, a system font, rather than the download failing over a typeface.
// A small script of its own (OFFLINE_SCRIPT) does what React did; none of the app's scripts come along.
// The share token and /api/ addresses never reach the file, so removing the link takes nothing away from it and the
// file gives nobody a way back to the server.
// A report with many long PDFs can pass what one JS string may hold, so the embedded files are never joined into one:
// each stands in the markup as a short mark and stays a Blob of its own, and the output is a Blob of parts.

export type OfflineHtmlInput = {
  page: HTMLElement;                       // the rendered page to copy
  title: string;
  reportNo: string;
  shareToken: string;                      // the one thing the file must not hold
  fileUrl: (fileId: string) => string;     // the public route for one attachment
  pdfPage: (data: ArrayBuffer, pageNo: number, targetW: number) => Promise<{ url: string; pages: number }>;
  thumbW: number;                          // the attachment box (RO_IMG_W × RO_IMG_H), and a PDF page in it
  thumbH: number;
  bigW: number;                            // a PDF page in the lightbox
  blue: string;                            // the attachment frame
  labels: { zonesShow: string; zonesHide: string; close: string };
};

const dataUrlOf = (blob: Blob): Promise<string> => new Promise((resolve, reject) => {
  const fr = new FileReader();
  fr.onload = () => resolve(String(fr.result));
  fr.onerror = () => reject(fr.error);
  fr.readAsDataURL(blob);
});
const read = async (url: string): Promise<Blob> => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.blob();
};
// Against the site's root, not the page's address: the page's address is the share link.
const absolute = (v: string): string => {
  try { return new URL(v, `${window.location.origin}/`).href; } catch { return ''; }
};

// ── The CSS ─────────────────────────────────────────────────────────────────────────────────────────
const FACE = /@font-face\s*\{[^}]*\}/gi;
const FONT_RANK: Record<string, number> = { woff2: 3, woff: 2, truetype: 1, ttf: 1, opentype: 1, otf: 1 };
type Fonts = Record<string, Promise<string>>;   // one fetch per file: the weights of a family often share one

// One @font-face with its font inside it, or nothing if the font could not be had. A face lists its file several times
// over for browsers long gone (the icon sheet: eot, woff2, woff, ttf); only the best one goes in.
async function faceWithFont(face: string, base: string, fonts: Fonts): Promise<string> {
  if (/url\(\s*['"]?data:/i.test(face)) return face;
  let best = ''; let bestRank = 0;
  const urls = /url\(\s*['"]?([^'")]+)['"]?\s*\)(?:\s*format\(\s*['"]?([\w-]+)['"]?\s*\))?/gi;
  let m: RegExpExecArray | null;
  while ((m = urls.exec(face))) {
    const ext = m[1].split(/[?#]/)[0].split('.').pop() || '';
    const rank = FONT_RANK[(m[2] || '').toLowerCase()] || FONT_RANK[ext.toLowerCase()] || 0;
    if (rank > bestRank) { best = m[1]; bestRank = rank; }
  }
  if (!best) return '';
  let url = '';
  try { url = new URL(best, base).href; } catch { return ''; }
  if (!fonts[url]) fonts[url] = read(url).then(dataUrlOf).catch(() => '');
  const data = await fonts[url];
  if (!data) return '';
  return face.replace(/src\s*:[^;}]*;?/gi, '').replace('{', () => `{src:url("${data}");`);
}
async function inlineFaces(css: string, base: string, fonts: Fonts): Promise<string> {
  const found: string[] = css.match(FACE) || [];
  const faces = await Promise.all(found.map(f => faceWithFont(f, base, fonts)));
  let i = 0;
  return css.replace(FACE, () => faces[i++]);
}
// Another site's sheet, read over the network because the browser will not let a page read its rules. No sheet, no
// harm: the page falls back to system fonts.
async function fetchedSheet(url: string, fonts: Fonts): Promise<string> {
  try {
    const res = await fetch(url);
    return res.ok ? await inlineFaces(await res.text(), url, fonts) : '';
  } catch { return ''; }
}
async function collectCss(): Promise<string> {
  const fonts: Fonts = {};
  const out: string[] = [];
  for (const sheet of Array.from(document.styleSheets)) {
    const base = sheet.href || `${window.location.origin}/`;
    let rules: CSSRule[] | null = null;
    try { rules = Array.from(sheet.cssRules); } catch { rules = null; }   // reading another site's rules throws
    if (!rules) {
      if (sheet.href) out.push(await fetchedSheet(sheet.href, fonts));
      continue;
    }
    // global.css opens with an @import from the font host; an @import in the file would be a trip to the network.
    for (const rule of rules.filter(r => r instanceof CSSImportRule) as CSSImportRule[]) {
      out.push(await fetchedSheet(new URL(rule.href, base).href, fonts));
    }
    const own = rules.filter(r => !(r instanceof CSSImportRule)).map(r => r.cssText).join('\n');
    out.push(await inlineFaces(own, base, fonts));
  }
  // Whatever still points outside the file (the app's sheets hold nothing else of the kind today) goes.
  return out.join('\n').replace(/url\(\s*['"]?(?!data:)[^'")]*['"]?\s*\)/gi, 'none');
}

// What the script below draws, in the look the live page gives it (FileImageLarge's lightbox).
const OFFLINE_CSS = `
.dd-off-lb{position:fixed;inset:0;z-index:10090;background:rgba(0,0,0,.82);display:flex;align-items:safe center;justify-content:center;cursor:zoom-out;overflow:auto}
.dd-off-lb-stack{display:flex;flex-direction:column;gap:14px;align-items:center;padding:4vh 0;cursor:default}
.dd-off-lb img{max-width:92vw;border-radius:6px;box-shadow:0 8px 40px rgba(0,0,0,.5);background:#fff}
.dd-off-lb img.dd-off-one{max-height:92vh}
.dd-off-lb-x{position:fixed;top:18px;right:22px;width:40px;height:40px;border-radius:999px;border:none;background:rgba(255,255,255,.92);color:#333;font-size:24px;line-height:1;cursor:pointer;z-index:1}
@media print{.dd-off-lb{display:none}}
`;

// ── What React did ──────────────────────────────────────────────────────────────────────────────────
// The jump pills (DDView's jump: JUMP_GAP, 12px, under the pinned strip), the lightbox (Esc, × or the backdrop closes
// it), the folded zones, the save arrow on a non-picture row, and each field's share of the columns. That last one is
// useDdFreeColumns' pass over again: the spans in the copy were measured on the screen that downloaded it, and a file
// opened in a narrower window would otherwise ask for columns that window does not have, the fields running off the
// sheet. The room under the last sheet (--dd-end-room) is DDView's sum again, with the strip as its one band, for the
// same reason: the copy carries the figure measured on the downloader's screen, and a taller window would stop short
// of parking that sheet under the strip. The blue pill (dd-pill-on, report 193ebe45) is DDView's reading line again,
// with the strip as its one band: the copy carries the class on whichever pill was being read at download, and the
// first pass takes it off that pill unless the reader is there too. The pills glide at DDView's pace (slowScrollTo,
// report 3f417898): its 0.35 is SCROLL_SPEED again, and the sum, the stop at the page's end and the take-over are the
// same. Plain ES5, and no user text in it: every label is an attribute the builder wrote, so the markup's own escaping
// covers it. It must never hold a closing script tag.
const OFFLINE_SCRIPT = `(function () {
  var d = document, root = d.querySelector('.dd-client-report');
  function all(sel, from) { return Array.prototype.slice.call((from || d).querySelectorAll(sel)); }

  var stopSlowScroll = null;
  function slowScrollTo(top) {
    if (stopSlowScroll) stopSlowScroll();
    var box = d.documentElement, from = box.scrollTop;
    var dist = Math.min(top, box.scrollHeight - box.clientHeight) - from;
    if (!dist) return;
    var ms = Math.sqrt(Math.abs(dist)) / 60 / 0.35 * 1000;
    var input = ['wheel', 'touchstart', 'keydown'], start = null, frame = 0;
    function stop() {
      cancelAnimationFrame(frame);
      input.forEach(function (type) { window.removeEventListener(type, stop, true); });
      stopSlowScroll = null;
    }
    function step(now) {
      if (start === null) start = now;
      var p = Math.min(1, (now - start) / ms);
      box.scrollTo({ top: from + dist * (p < 0.5 ? 2 * p * p : 1 - 2 * (1 - p) * (1 - p)) });
      if (p < 1) frame = requestAnimationFrame(step); else stop();
    }
    input.forEach(function (type) { window.addEventListener(type, stop, { capture: true, passive: true }); });
    stopSlowScroll = stop;
    frame = requestAnimationFrame(step);
  }

  var strip = d.querySelector('.dd-client-report > .dd-pager');
  if (strip) all('button', strip).forEach(function (pill, i) {
    pill.addEventListener('click', function () {
      var sec = d.getElementById('dd-page-' + i);
      if (!sec) return;
      var top = window.pageYOffset + sec.getBoundingClientRect().top - strip.getBoundingClientRect().height - 12;
      slowScrollTo(Math.max(0, top));
    });
  });

  var box = null;
  function closeBox() { if (box) { d.body.removeChild(box); box = null; } }
  function openBox(thumb) {
    closeBox();
    box = d.createElement('div');
    box.className = 'dd-off-lb';
    box.addEventListener('click', function (e) { if (e.target === box) closeBox(); });
    var stack = d.createElement('div');
    stack.className = 'dd-off-lb-stack';
    var pages = thumb.parentNode.querySelector('template');
    if (pages) stack.appendChild(d.importNode(pages.content, true));
    else { var big = d.createElement('img'); big.alt = ''; big.src = thumb.src; stack.appendChild(big); }
    if (stack.children.length === 1) stack.children[0].className = 'dd-off-one';
    var x = d.createElement('button');
    x.type = 'button';
    x.className = 'dd-off-lb-x';
    x.textContent = String.fromCharCode(215);
    x.title = d.querySelector('[data-dd-close]').getAttribute('data-dd-close');
    x.addEventListener('click', closeBox);
    box.appendChild(stack);
    box.appendChild(x);
    d.body.appendChild(box);
  }
  all('.dd-off-thumb').forEach(function (t) { t.addEventListener('click', function () { openBox(t); }); });
  d.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeBox(); });

  all('.dd-off-save').forEach(function (b) {
    b.addEventListener('click', function () { b.parentNode.querySelector('a[download]').click(); });
  });

  all('.dd-zone-toggle').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var body = btn.parentNode.querySelector('.dd-zone-fold-body');
      var open = body.style.display === 'none';
      body.style.display = open ? 'block' : 'none';
      btn.querySelector('i').className = 'ti ti-' + (open ? 'x' : 'chevron-down');
      btn.lastChild.nodeValue = btn.getAttribute(open ? 'data-hide' : 'data-show');
    });
  });

  var FIELDS = ':scope > .dd-grid:not(.dd-grid-fixed) > .dd-cell:not(.dd-cell-full)';
  function spans() {
    all('.dd-cell', root).forEach(function (c) { c.style.setProperty('--dd-span', '1'); });
    var fields = [], opened = [];
    all('.dd-flow', root).forEach(function (flow) {
      var cs = getComputedStyle(flow);
      if (cs.display !== 'grid') return;
      var tracks = cs.gridTemplateColumns.split(' ').map(function (v) { return parseFloat(v); });
      if (tracks.length < 2 || tracks.some(function (w) { return !(w > 0); })) return;
      var gap = parseFloat(cs.columnGap) || 0;
      all(FIELDS, flow).forEach(function (cell) {
        var boxes = all('*', cell).filter(function (el) { return !el.children.length && (el.textContent || '').trim() !== ''; });
        fields.push({ cell: cell, columns: tracks.length, step: tracks[0] + gap, gap: gap, boxes: boxes });
      });
    });
    fields.forEach(function (f) {
      f.boxes.forEach(function (el) { opened.push({ el: el, ws: el.style.whiteSpace, w: el.style.width, mw: el.style.maxWidth }); });
    });
    opened.forEach(function (b) { b.el.style.whiteSpace = 'pre'; b.el.style.width = 'max-content'; b.el.style.maxWidth = 'none'; });
    fields.forEach(function (f) {
      var left = f.cell.getBoundingClientRect().left;
      f.want = f.boxes.reduce(function (n, el) { var r = el.getBoundingClientRect(); return Math.max(n, r.left - left + r.width); }, 0);
    });
    opened.forEach(function (b) { b.el.style.whiteSpace = b.ws; b.el.style.width = b.w; b.el.style.maxWidth = b.mw; });
    fields.forEach(function (f) {
      var need = Math.ceil((f.want - 2 + f.gap) / f.step);
      var pics = Number(f.cell.getAttribute('data-dd-pics')) || 1;
      f.cell.style.setProperty('--dd-span', String(Math.min(f.columns, Math.max(need, pics))));
    });
  }
  var lastW = root.clientWidth;
  window.addEventListener('resize', function () { if (root.clientWidth !== lastW) { lastW = root.clientWidth; spans(); } });
  spans();
  if (d.fonts && d.fonts.ready) d.fonts.ready.then(spans);

  var sheets = all('.dd-client-report > .dd-section'), lastSheet = sheets[sheets.length - 1];
  function endRoom() {
    var below = 0;
    for (var p = root; p !== d.documentElement; p = p.parentNode) {
      var st = getComputedStyle(p);
      below += (parseFloat(st.marginBottom) || 0) + (p === root ? 0 : parseFloat(st.paddingBottom) || 0);
    }
    var band = strip ? strip.getBoundingClientRect().height : 0;
    var room = Math.ceil(window.innerHeight - band - 12 - lastSheet.getBoundingClientRect().height - below);
    if (room > 12) root.style.setProperty('--dd-end-room', room + 'px');
    else root.style.removeProperty('--dd-end-room');
  }
  endRoom();
  window.addEventListener('resize', endRoom);
  if (window.ResizeObserver) new ResizeObserver(endRoom).observe(lastSheet);

  function markPill() {
    var pills = all('button', strip), on = 0;
    var line = Math.max(90, strip.getBoundingClientRect().height + 12 + 8);
    for (var i = 0; i < pills.length; i++) {
      var sec = d.getElementById('dd-page-' + i);
      if (!sec) continue;
      var r = sec.getBoundingClientRect();
      if (r.top <= line) on = i;
      if (r.top <= line && r.bottom > line) { on = i; break; }
    }
    pills.forEach(function (pill, n) { pill.classList.toggle('dd-pill-on', n === on); });
  }
  if (strip) {
    window.addEventListener('scroll', markPill, { passive: true });
    markPill();
  }
})();`;

// ── The file ────────────────────────────────────────────────────────────────────────────────────────
const fileNamePart = (s: string): string => String(s || '').replace(/[/\\:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();

// `missing` counts what could not be embedded: the button says so rather than handing over a quietly incomplete file.
// Each such box keeps the grey icon the live page shows for a file it cannot draw, and a PDF that pdf.js cannot open
// counts too.
export async function buildOfflineHtml(o: OfflineHtmlInput): Promise<{ blob: Blob; name: string; missing: number }> {
  // An inert document: nothing imported into it loads an image, so copying the page starts no download of its own, and
  // a relative address means nothing in it, which is why addresses are read with getAttribute and absolute(), never
  // img.src.
  const doc = document.implementation.createHTMLDocument('');
  const page = doc.importNode(o.page, true);
  const drop = (el: Element) => { if (el.parentNode) el.parentNode.removeChild(el); };
  Array.from(page.querySelectorAll('[data-dd-off-drop], script')).forEach(drop);

  const mark = `ddoff${Math.random().toString(36).slice(2)}x`;
  const embedded: Blob[] = [];
  const embed = (dataUrl: string): string => { embedded.push(new Blob([dataUrl])); return `${mark}${embedded.length - 1}x`; };
  let missing = 0;

  // A box is built afresh whatever state the live one was in: its thumbnail may still be spinning.
  const thumbCss = `display:block;width:${o.thumbW}px;height:${o.thumbH}px;object-fit:cover;border-radius:5px;outline:1px solid ${o.blue};`;
  for (const box of Array.from(page.querySelectorAll<HTMLElement>('[data-dd-box]'))) {
    const wrap = doc.createElement('div');
    wrap.setAttribute('style', `position:relative;width:${o.thumbW}px;margin-top:6px`);
    try {
      const blob = await read(o.fileUrl(box.getAttribute('data-dd-box') || ''));
      const thumb = doc.createElement('img');
      thumb.className = 'dd-off-thumb';
      thumb.setAttribute('alt', '');
      thumb.setAttribute('style', `${thumbCss}background:#fff;cursor:zoom-in`);
      wrap.appendChild(thumb);
      if (box.hasAttribute('data-dd-pdf')) {
        const bytes = await blob.arrayBuffer();
        const first = await o.pdfPage(bytes, 1, o.thumbW);
        thumb.setAttribute('src', embed(first.url));
        // A template holds the pages inert, so a long PDF costs the reader nothing until its lightbox opens.
        const pages = doc.createElement('template');
        for (let n = 1; n <= first.pages; n++) {
          const pg = doc.createElement('img');
          pg.setAttribute('alt', '');
          pg.setAttribute('src', embed((await o.pdfPage(bytes, n, o.bigW)).url));
          pages.content.appendChild(pg);
        }
        wrap.appendChild(pages);
      } else {
        thumb.setAttribute('src', embed(await dataUrlOf(blob)));
      }
    } catch {
      missing++;
      wrap.innerHTML = '';
      const none = doc.createElement('div');
      none.setAttribute('style', `${thumbCss}display:flex;align-items:center;justify-content:center;background:#f4f4f4`);
      none.innerHTML = '<i class="ti ti-file-text" style="font-size:26px;color:#b9b9b9"></i>';
      wrap.appendChild(none);
    }
    box.parentNode.replaceChild(wrap, box);
  }

  // A non-picture row: its name becomes a link that saves the file, and the arrow beside it presses that link, so the
  // file is in the output once. A file that could not be read keeps its name as plain words, and no arrow.
  for (const row of Array.from(page.querySelectorAll<HTMLElement>('[data-dd-row]'))) {
    const [nameBtn, saveBtn] = Array.from(row.querySelectorAll('button'));
    const filename = nameBtn.getAttribute('title') || nameBtn.textContent || 'file';
    let href = '';
    try { href = embed(await dataUrlOf(await read(o.fileUrl(row.getAttribute('data-dd-row') || '')))); } catch { missing++; }
    const link = doc.createElement(href ? 'a' : 'span');
    link.setAttribute('style', nameBtn.getAttribute('style') || '');
    link.setAttribute('title', filename);
    link.textContent = filename;
    if (href) { link.setAttribute('href', href); link.setAttribute('download', filename); }
    nameBtn.parentNode.replaceChild(link, nameBtn);
    if (saveBtn) { if (href) saveBtn.classList.add('dd-off-save'); else drop(saveBtn); }
    row.removeAttribute('data-dd-row');
  }

  // Any other picture: the zoning map, or one typed into a comment. A map on another website that refuses to be read
  // cannot be embedded from here at all; it keeps its full address, so it still shows on a connected machine, and it
  // counts as missing.
  for (const img of Array.from(page.querySelectorAll('img'))) {
    const src = img.getAttribute('src') || '';
    if (!src || src.indexOf(mark) === 0 || /^data:/i.test(src)) continue;
    const url = absolute(src);
    try { img.setAttribute('src', embed(await dataUrlOf(await read(url)))); } catch { missing++; img.setAttribute('src', url); }
  }

  // Links stay links, but a path on this site means nothing in a file, so it takes the site's address. Whatever would
  // lead back through the share link or the API goes: this sweep is what makes "neither is in the file" true of every
  // address, including ones nobody thought of, such as a link typed into a comment.
  const leads = (v: string) => v.indexOf(o.shareToken) >= 0 || v.indexOf('/api/') >= 0;
  Array.from(page.querySelectorAll('[href], [src]')).forEach(el => ['href', 'src'].forEach(attr => {
    const v = el.getAttribute(attr);
    if (!v || v.indexOf(mark) === 0 || /^(#|data:|mailto:|tel:)/i.test(v)) return;
    const abs = absolute(v);
    if (!abs || leads(abs)) el.removeAttribute(attr); else el.setAttribute(attr, abs);
  }));

  Array.from(page.querySelectorAll('.dd-zone-toggle')).forEach(btn => {
    btn.setAttribute('data-show', o.labels.zonesShow);
    btn.setAttribute('data-hide', o.labels.zonesHide);
  });
  page.setAttribute('data-dd-close', o.labels.close);

  const css = await collectCss();
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;');
  const parts: BlobPart[] = [
    '<!DOCTYPE html>\n<html lang="en"><head><meta charset="utf-8">',
    '<meta name="viewport" content="width=device-width, initial-scale=1">',
    `<title>${esc(o.title)}</title>`,
    '<style>', css, OFFLINE_CSS, '</style></head><body><div id="root">',
  ];
  // split() with a capture hands back markup, number, markup, number…
  page.outerHTML.split(o.shareToken).join('').split(new RegExp(`${mark}(\\d+)x`))
    .forEach((piece, i) => parts.push(i % 2 ? embedded[Number(piece)] : piece));
  parts.push('</div><script>', OFFLINE_SCRIPT, '</script></body></html>');

  const name = `${[o.title, o.reportNo].map(fileNamePart).filter(Boolean).join(' - ')}.html`;
  return { blob: new Blob(parts, { type: 'text/html;charset=utf-8' }), name, missing };
}
